import React, { useState, forwardRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  InputBase,
  Alert,
  alpha,
  useTheme
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ExitToApp as ExitIcon,
  SmartToy as BotIcon,
  Memory as ThreadIcon
} from '@mui/icons-material';
import { Scenario } from '../../types/attacks';
import { AttackStatus } from '../../types/operations';
import AttackConfigForm from '../AttackConfigForm';
import { describeResourceCaps, hasResourceCaps } from '../../utils/resourceCaps';

interface ControlPanelProps {
  scenario: Scenario;
  botCount: number;
  threadCount: number;
  botStatus: {
    pending: number;
    running: number;
    total: number;
  };
  attackStatus: AttackStatus;
  attackConfig: Record<string, any>;
  onBotCountChange: (count: number) => void;
  onThreadCountChange: (count: number) => void;
  onApplyCounts?: (botCount: number, threadCount: number) => void;
  isApplyingCounts?: boolean;
  onStart: () => void;
  onStartAttack?: (botCount: number, threadCount: number) => Promise<void>;
  onPause: () => void;
  onEnd: () => void;
  onEndScenario: () => void;
  onConfigUpdate: (config: Record<string, any>) => void;
  onApplyConfig?: (config: Record<string, any>) => Promise<void> | void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const ControlPanel = forwardRef<HTMLDivElement, ControlPanelProps>(({
  scenario,
  botCount,
  threadCount,
  botStatus,
  attackStatus,
  attackConfig,
  onBotCountChange,
  onThreadCountChange,
  onApplyCounts,
  isApplyingCounts,
  onStart,
  onPause,
  onEnd,
  onEndScenario,
  onConfigUpdate,
  onApplyConfig,
  onDirtyChange
}, ref) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Track if config is dirty
  const [originalConfig, setOriginalConfig] = useState<Record<string, any>>(attackConfig);
  const [configDirty, setConfigDirty] = useState(false);
  const [isApplyingConfig, setIsApplyingConfig] = useState(false);

  const areConfigsEqual = React.useCallback((a: Record<string, any>, b: Record<string, any>) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }, []);

  // Helper function to ensure valid numeric string - memoized to prevent re-creation
  const toValidNumericString = React.useCallback((value: any, fallback: number): string => {
    const num = typeof value === 'number' ? value : parseInt(String(value), 10);
    return !isNaN(num) && num >= 0 ? String(num) : String(fallback);
  }, []);

  const [botInputValue, setBotInputValue] = useState(toValidNumericString(botCount, 0));
  const [threadInputValue, setThreadInputValue] = useState(toValidNumericString(threadCount, 1));
  const prevBotCountRef = React.useRef(botCount);
  const prevThreadCountRef = React.useRef(threadCount);
  
  // Track if user is actively editing inputs (prevent auto-sync)
  const [isManualEditing, setIsManualEditing] = useState(false);

  // Memoize parsed values to prevent recalculation on every render
  const parsedBotValue = React.useMemo(() => Number.parseInt(botInputValue, 10), [botInputValue]);
  const parsedThreadValue = React.useMemo(() => Number.parseInt(threadInputValue, 10), [threadInputValue]);
  const botValid = React.useMemo(() => !Number.isNaN(parsedBotValue) && parsedBotValue >= 0, [parsedBotValue]);
  const threadValid = React.useMemo(() => !Number.isNaN(parsedThreadValue) && parsedThreadValue >= 1, [parsedThreadValue]);
  const countsDirty = React.useMemo(() =>
    botValid &&
    threadValid &&
    (parsedBotValue !== botCount || parsedThreadValue !== threadCount),
    [botValid, threadValid, parsedBotValue, botCount, parsedThreadValue, threadCount]
  );

  // Sync input values when props change - only update if NOT actively editing
  React.useEffect(() => {
    // Don't sync if user is actively editing in manual mode
    if (isManualEditing) return;
    
    const validValue = toValidNumericString(botCount, 0);
    if (botInputValue !== validValue) {
      setBotInputValue(validValue);
    }
    prevBotCountRef.current = botCount;
  }, [botCount, isManualEditing, botInputValue, toValidNumericString]);

  React.useEffect(() => {
    // Don't sync if user is actively editing in manual mode
    if (isManualEditing) return;
    
    const validValue = toValidNumericString(threadCount, 1);
    if (threadInputValue !== validValue) {
      setThreadInputValue(validValue);
    }
    prevThreadCountRef.current = threadCount;
  }, [threadCount, isManualEditing, threadInputValue, toValidNumericString]);

  // `threads` is both this counter and a field of the configuration, so the JSON view can change
  // it behind the counter's back. The sync effect above deliberately ignores prop changes while
  // the operator is mid-edit, and that flag is sticky across the *other* counter — editing the
  // bot count leaves it set — so a JSON edit could leave the counter showing the old number.
  // Applying then folds that stale number back into the configuration and silently undoes the
  // JSON edit (CAO-237). A configuration value that actually changed is the operator editing the
  // same number through the other view, never a stale prop, so adopt it unconditionally. The
  // first value a scenario's configuration brings is excluded: that is the initial load landing,
  // which must not overwrite a count the operator is already typing.
  const prevConfigThreadsRef = React.useRef<unknown>(attackConfig.threads);
  React.useEffect(() => {
    const configThreads = attackConfig.threads;
    const previous = prevConfigThreadsRef.current;
    if (configThreads === previous) return;
    prevConfigThreadsRef.current = configThreads;
    if (previous === undefined) return;
    if (typeof configThreads !== 'number' || !Number.isInteger(configThreads) || configThreads < 1) return;
    setThreadInputValue(String(configThreads));
  }, [attackConfig.threads]);

  const applyCounts = React.useCallback(async () => {
    if (!botValid || !threadValid) return;
    if (onApplyCounts) {
      await Promise.resolve(onApplyCounts(parsedBotValue, parsedThreadValue));
      return;
    }
    onBotCountChange(parsedBotValue);
    onThreadCountChange(parsedThreadValue);
  }, [botValid, threadValid, onApplyCounts, parsedBotValue, parsedThreadValue, onBotCountChange, onThreadCountChange]);

  // The operator has typed a thread count that hasn't been applied yet.
  const threadsDirty = React.useMemo(
    () => threadValid && parsedThreadValue !== threadCount,
    [threadValid, parsedThreadValue, threadCount]
  );

  const applyConfig = React.useCallback(async () => {
    if (!configDirty && !threadsDirty) return;
    // A configuration that hasn't loaded yet (the panel renders while the scenario's S3 config
    // is still in flight) must never be written back: the write replaces the stored
    // configuration wholesale, so sending `{ threads: n }` alone would wipe targets and flags.
    // The scaling call has already persisted the count in that case.
    if (Object.keys(attackConfig).length === 0) return;

    // Threads live in the same configuration object the JSON editor edits, but the form renders
    // them as a standalone counter next to the bot count. Fold the counter's value in, so
    // applying the form writes `threads` into the very representation the JSON view shows and
    // the backend persists (CAO-237). Without this the form's value only reached the scaling
    // call, and the next configuration write put the stale `threads` back.
    const nextConfig = threadValid && attackConfig.threads !== parsedThreadValue
      ? { ...attackConfig, threads: parsedThreadValue }
      : attackConfig;

    // Keep the shared configuration (and therefore the JSON view) in step with the counter
    // even when there is nothing to persist to.
    if (nextConfig !== attackConfig) {
      onConfigUpdate(nextConfig);
    }
    if (!onApplyConfig) return;

    setIsApplyingConfig(true);
    try {
      await Promise.resolve(onApplyConfig(nextConfig));
      setOriginalConfig(nextConfig);
      setConfigDirty(false);
    } finally {
      setIsApplyingConfig(false);
    }
  }, [attackConfig, configDirty, onApplyConfig, onConfigUpdate, parsedThreadValue, threadValid, threadsDirty]);

  // Applying the form means both halves of it: scale bots/threads, then persist the
  // configuration (including the thread count that just changed).
  const applyCountsAndConfig = React.useCallback(async () => {
    await applyCounts();
    await applyConfig();
  }, [applyCounts, applyConfig]);

  // Wrapped config update that marks config as dirty
  const handleConfigUpdate = React.useCallback((config: Record<string, any>) => {
    setConfigDirty(!areConfigsEqual(config, originalConfig));
    onConfigUpdate(config);
  }, [areConfigsEqual, onConfigUpdate, originalConfig]);

  // Reset config to original
  const resetConfig = React.useCallback(() => {
    setConfigDirty(false);
    onConfigUpdate(originalConfig);
  }, [originalConfig, onConfigUpdate]);

  // Update original config when prop changes (but not when we're dirty)
  React.useEffect(() => {
    if (!configDirty && !countsDirty) {
      setOriginalConfig(attackConfig);
    }
  }, [attackConfig, configDirty, countsDirty]);

  // Notify parent of combined dirty state
  React.useEffect(() => {
    const isAnyDirty = countsDirty || configDirty;
    onDirtyChange?.(isAnyDirty);
  }, [countsDirty, configDirty, onDirtyChange]);

  // Show Save/Cancel buttons when:
  // 1. Attack is stopped (user needs to apply initial values)
  // 2. OR user has manually edited (countsDirty or configDirty)
  const manualModeDirty = attackStatus === 'stopped' || countsDirty || configDirty;
  const isUpdating = Boolean(isApplyingCounts) || isApplyingConfig;

  return (
    <Card
      ref={ref}
      sx={{
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(12px)',
        border: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.18)}`,
        transition: 'all 0.3s ease',
      }}
    >
      <CardContent>
        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'stretch' }}>
          {/* Left: Bot and Thread Counters (75% width) */}
          <Box sx={{ display: 'flex', gap: 3, flex: '3 1 0', justifyContent: 'center' }}>
            {/* Bots */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 4,
                py: 2.5,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.08),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: 'all 0.2s ease',
                flex: '1 1 0',
                '&:hover': {
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                  boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
              }}
            >
              <BotIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '2.5rem',
                  filter: `drop-shadow(0 0 10px ${alpha(theme.palette.primary.main, 0.6)})`,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: '"Space Mono", monospace',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: '0.7rem',
                      display: 'block',
                    }}
                  >
                    Running Bots
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.65rem',
                      display: 'block',
                    }}
                  >
                    Running: {botStatus.running} | Pending: {botStatus.pending}
                  </Typography>
                </Box>
                <InputBase
                  type="number"
                  value={botInputValue}
                  onChange={(e) => {
                    setIsManualEditing(true);
                    setBotInputValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void applyCountsAndConfig();
                      setIsManualEditing(false);
                    }
                  }}
                  onBlur={() => {
                    // Don't reset if user is still editing (dirty state)
                    if (countsDirty) return;
                    // Reset to current value if empty
                    const validCurrentValue = toValidNumericString(botCount, 0);
                    if (botInputValue === '') {
                      setBotInputValue(validCurrentValue);
                    }
                    setIsManualEditing(false);
                  }}
                  inputProps={{
                    min: 0,
                    'aria-label': 'Running bots',
                    style: {
                      textAlign: 'center',
                      padding: 0,
                    }
                  }}
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    textShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.6)}`,
                    width: '100%',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: '2px',
                      backgroundColor: theme.palette.primary.main,
                      transition: 'width 0.2s ease',
                    },
                    '&:hover::after': {
                      width: '80%',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Threads */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 4,
                py: 2.5,
                borderRadius: 2,
                background: alpha(theme.palette.secondary.main, 0.08),
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                transition: 'all 0.2s ease',
                flex: '1 1 0',
                '&:hover': {
                  border: `2px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
                  boxShadow: `0 0 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                },
              }}
            >
              <ThreadIcon
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: '2.5rem',
                  filter: `drop-shadow(0 0 10px ${alpha(theme.palette.secondary.main, 0.6)})`,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: '"Space Mono", monospace',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: '0.7rem',
                    display: 'block',
                  }}
                >
                  Threads
                </Typography>
                <InputBase
                  type="number"
                  value={threadInputValue}
                  onChange={(e) => {
                    setIsManualEditing(true);
                    setThreadInputValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void applyCountsAndConfig();
                      setIsManualEditing(false);
                    }
                  }}
                  onBlur={() => {
                    // Don't reset if user is still editing (dirty state)
                    if (countsDirty) return;
                    // Reset to current value if empty
                    const validCurrentValue = toValidNumericString(threadCount, 1);
                    if (threadInputValue === '') {
                      setThreadInputValue(validCurrentValue);
                    }
                    setIsManualEditing(false);
                  }}
                  inputProps={{
                    min: 1,
                    'aria-label': 'Threads per bot',
                    style: {
                      textAlign: 'center',
                      padding: 0,
                    }
                  }}
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: theme.palette.secondary.main,
                    textShadow: `0 0 15px ${alpha(theme.palette.secondary.main, 0.6)}`,
                    width: '100%',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: '2px',
                      backgroundColor: theme.palette.secondary.main,
                      transition: 'width 0.2s ease',
                    },
                    '&:hover::after': {
                      width: '80%',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Right: Action Buttons Stacked (25% width) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: '1 1 0' }}>
            {manualModeDirty ? (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  onClick={async () => {
                    await applyCountsAndConfig();
                    setIsManualEditing(false);
                  }}
                  disabled={isUpdating}
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    py: 1.5,
                    fontSize: '0.85rem',
                    flex: '1 1 0',
                  }}
                >
                  {isUpdating ? 'Updating' : 'Update'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setBotInputValue(toValidNumericString(botCount, 0));
                    setThreadInputValue(toValidNumericString(threadCount, 1));
                    resetConfig();
                    setIsManualEditing(false);
                  }}
                  disabled={isUpdating}
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    py: 1.5,
                    fontSize: '0.85rem',
                    flex: '1 1 0',
                    borderColor: 'text.secondary',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'text.primary',
                    },
                  }}
                >
                  Clear
                </Button>
              </>
            ) : (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={attackStatus === 'running' ? <PauseIcon /> : <PlayIcon />}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (attackStatus === 'running') {
                      onPause();
                    } else {
                      // Apply counts then start
                      applyCounts();
                      setIsManualEditing(false);
                      onStart();
                    }
                  }}
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    py: 1.5,
                    fontSize: '0.85rem',
                    flex: '1 1 0',
                  }}
                >
                  {attackStatus === 'running' ? 'Pause' : 'Save'}
                </Button>
                
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  color="error"
                  startIcon={<ExitIcon />}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onEndScenario();
                  }}
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    py: 1.5,
                    fontSize: '0.85rem',
                    flex: '1 1 0',
                  }}
                >
                  End
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Resource Caps (CAO-202) — read-only; edited from the scenario editor */}
        {hasResourceCaps(scenario) && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              background: alpha(theme.palette.warning.main, 0.05),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.75,
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '0.7rem',
              }}
            >
              Resource Caps (per bot)
            </Typography>
            {describeResourceCaps(scenario, botCount).map((line) => (
              <Typography
                key={line}
                variant="body2"
                sx={{ fontFamily: '"Space Mono", monospace', color: theme.palette.warning.main, lineHeight: 1.6 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}

        <Divider
          sx={{
            mb: 4,
            mt: 3,
            background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.3)}, transparent)`,
          }}
        />

        {/* Attack Configuration */}
        {scenario.attack_type ? (
          <AttackConfigForm
            attackType={scenario.attack_type}
            configuration={attackConfig}
            onChange={handleConfigUpdate}
            isEditMode={false}
            restrictedFields={[]}
            showConfigPreview={true}
          />
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No attack type configured for this scenario
          </Alert>
        )}
      </CardContent>
    </Card>
  );
});

ControlPanel.displayName = 'ControlPanel';

export default ControlPanel;

